import { useEffect } from "react";
import { useLocation } from "react-router-dom";

interface SEOData {
  title: string;
  description: string;
  keywords?: string;
  path?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  canonical?: string;
}

/** Production origin used for absolute canonical / OG URLs. */
export const SITE_ORIGIN = "https://urfx.io";

/** Absolute OG image — crawlers require a full HTTPS URL. */
export const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/og-image.png`;

function normalizePath(path: string): string {
  if (!path || path === "/") return "/";
  const clean = path.split("?")[0].split("#")[0];
  return clean.length > 1 && clean.endsWith("/") ? clean.slice(0, -1) : clean;
}

export function toCanonicalUrl(path = "/"): string {
  const normalized = normalizePath(path);
  return normalized === "/" ? `${SITE_ORIGIN}/` : `${SITE_ORIGIN}${normalized}`;
}

const updateMetaTag = (
  attribute: string,
  attributeValue: string,
  content: string,
) => {
  let element = document.querySelector(
    `meta[${attribute}="${attributeValue}"]`,
  ) as HTMLMetaElement | null;

  if (element) {
    element.content = content;
  } else {
    element = document.createElement("meta");
    element.setAttribute(attribute, attributeValue);
    element.content = content;
    document.head.appendChild(element);
  }
};

export const updateCanonical = (pathOrUrl: string) => {
  const href =
    pathOrUrl.startsWith("http://") || pathOrUrl.startsWith("https://")
      ? pathOrUrl
      : toCanonicalUrl(pathOrUrl);

  let link = document.querySelector(
    'link[rel="canonical"]',
  ) as HTMLLinkElement | null;

  if (link) {
    link.href = href;
  } else {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    link.href = href;
    document.head.appendChild(link);
  }

  updateMetaTag("property", "og:url", href);
  updateMetaTag("name", "twitter:url", href);
};

export const updateMetaTags = (seo: SEOData, path?: string) => {
  document.title = seo.title;
  updateMetaTag("name", "description", seo.description);

  if (seo.keywords) {
    updateMetaTag("name", "keywords", seo.keywords);
  }

  const ogImage = seo.ogImage || DEFAULT_OG_IMAGE;
  const canonicalPath = seo.canonical || path || seo.path || "/";

  updateMetaTag("property", "og:title", seo.ogTitle || seo.title);
  updateMetaTag(
    "property",
    "og:description",
    seo.ogDescription || seo.description,
  );
  updateMetaTag("property", "og:type", "website");
  updateMetaTag("property", "og:site_name", "URFX");
  updateMetaTag("property", "og:image", ogImage);
  updateMetaTag("property", "og:image:secure_url", ogImage);

  updateMetaTag("name", "twitter:card", "summary_large_image");
  updateMetaTag("name", "twitter:title", seo.twitterTitle || seo.title);
  updateMetaTag(
    "name",
    "twitter:description",
    seo.twitterDescription || seo.description,
  );
  updateMetaTag("name", "twitter:image", ogImage);

  updateCanonical(canonicalPath);
};

// SEO data for different pages
export const seoData = {
  home: {
    title:
      "Instant Funding Prop Firm | Trade Instantly with URFX AI-Powered Accounts",
    description:
      "Join URFX, a leading instant funding prop firm offering no-challenge accounts, AI-powered risk management, and fair profit splits. Start trading instantly today.",
    keywords:
      "prop trading firm, prop firm trading challenge, prop firm challenge, prop firm trading, best prop trading firm, futures trading prop firm",
    path: "/",
  },
  programs: {
    title: "URFX Programs | Instant Funding & Prop Firm Challenges",
    description:
      "Explore URFX trading programs including Instant Funding, One-Step, and Two-Step challenges. Choose the account size and path that fits your trading style.",
    path: "/programs",
  },
  velocity: {
    title: "URFX Velocity | Pay Only When You Pass",
    description:
      "Velocity is URFX's low-cost evaluation program. Start with a small fee, prove your skill, and only pay the funded account activation fee once you've passed.",
    path: "/velocity",
  },
  pricing: {
    title: "URFX Pricing | Instant Funded Forex Accounts with No Challenge",
    description:
      "Get instant access to funded forex accounts with URFX. No challenges, no upfront fees—start trading up to $1M with transparent pricing and AI-powered risk management.",
    path: "/pricing",
  },
  promo: {
    title: "URFX Promo Offers | Limited-Time Prop Firm Deals",
    description:
      "Check current URFX promo offers and discounts on funded accounts and evaluation challenges. Limited-time deals for new and returning traders.",
    path: "/promo",
  },
  contactUs: {
    title: "Contact URFX | Get Instant Funding Support & Inquiries",
    description:
      "Reach out to URFX for questions on instant‑funded forex accounts, AI risk tools, account setup, payouts, or support. Fast responses for traders worldwide.",
    path: "/contact-us",
  },
  support: {
    title: "Support | URFX Help & Trader Assistance",
    description:
      "Get help with URFX funded accounts, platform issues, payouts, and general support. Reach our team through the contact page and resources.",
    path: "/support",
  },
  aboutUs: {
    title: "About URFX | AI‑Powered Prop Firm & Instant Funded Trading",
    description:
      "Discover URFX: the world's first AI‑powered prop firm offering instant‑funded forex accounts, fast payouts, MetaTrader access, and up to $1 M in capital.",
    path: "/about-us",
  },
  faq: {
    title: "URFX FAQ | Instant Funded Account Rules & Trading Help",
    description:
      "Find answers to common questions about URFX instant-funded forex accounts, drawdown limits, payouts, platforms, trading rules, and support options.",
    path: "/faqs",
  },
  testimonials: {
    title: "URFX Testimonials | Real Trader Reviews & Success Stories",
    description:
      "Read trader testimonials and success stories from URFX funded accounts. See why traders choose URFX for instant funding and prop firm challenges.",
    path: "/testimonials",
  },
  tradingRules: {
    title: "URFX Trading Rules | Instant Funded Accounts Terms & Limits",
    description:
      "Explore URFX trading rules: drawdown and profit limits, allowed instruments, payout thresholds, platform usage (MT4/MT5), and risk management policies.",
    path: "/trading-rules",
  },
  termsConditions: {
    title: "URFX Terms & Conditions | Forex Funding Account Rules",
    description:
      "Review URFX's Terms & Conditions covering account eligibility, funding usage, risk limits, trading restrictions, payouts, and legal responsibilities.",
    path: "/terms-&-conditions",
  },
  privacyPolicy: {
    title: "URFX Privacy Policy | Data & Security for Forex Accounts",
    description:
      "Read URFX's Privacy Policy to learn how your data is collected, used, stored, and protected when you open a funded forex trading account—compliance and transparency guaranteed.",
    path: "/privacy-policy",
  },
  becomePartner: {
    title: "Partner with URFX | Forex Prop Firm Affiliate & Funding Partners",
    description:
      "Join URFX's partner program to earn commissions by promoting instant-funded forex accounts. Ideal for affiliates, broker referrals, educators & trading communities.",
    path: "/become-a-partner",
  },
} as const satisfies Record<string, SEOData>;

export type SeoPageKey = keyof typeof seoData;

/** Map pathname -> SEO page key for public marketing routes. */
export const PATH_SEO_MAP: Record<string, SeoPageKey> = {
  "/": "home",
  "/programs": "programs",
  "/velocity": "velocity",
  "/pricing": "pricing",
  "/promo": "promo",
  "/contact-us": "contactUs",
  "/support": "support",
  "/about-us": "aboutUs",
  "/faqs": "faq",
  "/testimonials": "testimonials",
  "/trading-rules": "tradingRules",
  "/terms-&-conditions": "termsConditions",
  "/privacy-policy": "privacyPolicy",
  "/become-a-partner": "becomePartner",
};

/**
 * Keeps <link rel="canonical"> + og:url aligned with the active route.
 * Mount once inside the public Layout.
 */
export function useDocumentSeo() {
  const { pathname } = useLocation();

  useEffect(() => {
    const path = normalizePath(pathname);
    const key = PATH_SEO_MAP[path];

    if (key) {
      updateMetaTags(seoData[key], path);
      return;
    }

    // Auth / private areas: still self-canonicalize to avoid homepage leakage
    updateCanonical(path);
  }, [pathname]);
}

// Hook to use SEO data
export const useSEO = (pageKey: SeoPageKey) => {
  useEffect(() => {
    const data = seoData[pageKey];
    if (data) {
      updateMetaTags(data, data.path);
    }
  }, [pageKey]);
};
